import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, baixarBlob, ErroApi } from '../api';
import {
  ACONDICIONAMENTO, dataHora, paraNumero,
  plural, rotulo, rotuloDoAcondicionamento, TIPOS_AMOSTRA,
} from '../formato';
import {
  descartarPrevias, enviarFotos, MINIMO, ordenarParaEnvio, Progresso, ROTEIRO, RoteiroFotos,
  type FotoPendente,
} from '../Fotos';
import { EscopoDaOs } from './FaixaDoEscopo';
import { QuadroDeAmostras } from './QuadroDeAmostras';
import { nomeDaAmostra } from '../NomeDaAmostra';
import { useSessao } from '../sessao';
import { useCaminho } from '../Caminho';
import {
  Area, Aviso, Campo, Carregando, Dialogo, ErroDaApi, Escolha,
  FotoProtegida, JanelaDocumento, LinkProtegido, type Opcao, Selo, type Tom, useAbasQueGrudam, Vazio,
} from '../ui';
import { ExcluirDeVez } from './ExclusaoDefinitiva';
import { LigarPrevistas } from './LigarPrevistas';

/**
 * A ficha do recebimento.
 *
 * É onde o recebimento vira documento: conferir o que foi registrado,
 * completar o roteiro fotográfico do que ficou faltando, pré-visualizar,
 * emitir o PDF e imprimir as etiquetas.
 *
 * Nada aqui apaga nada. A foto anexada no lote errado se DESCONSIDERA, com
 * motivo — sai do documento e continua auditável (migração `0008`).
 */

interface Amostra {
  id: string; codigo: string;
  identificacao_recebida: string | null;
  ponto_investigacao: string | null;
  profundidade_inicial: string | null;
  profundidade_final: string | null;
  camada: string | null;
  tipo_amostra: string | null;
  tipo_acondicionamento: string | null;
  quantidade_aproximada: string | null;
  quantidade_unidade: string | null;
  data_coleta: string | null;
  status: string;
  localizacao_fisica: string | null;
  condicoes_embalagem: string | null;
  acondicionamento_descricao: string | null;
}

interface Lote {
  id: string; codigo: string; decisao: string; status: string;
  origem: string; datahora_recebimento: string;
  cliente: string | null; cliente_id: string | null;
  obra_codigo: string | null; obra_nome: string | null;
  os_id: string | null; os_numero: string | null;
  amostras_sem_minimo: string | number;
  responsavel_entrega_nome: string | null; responsavel_entrega_doc: string | null;
  responsavel_entrega_empresa?: string | null;
  entrega_equipe_id?: string | null;
  entrega_equipe_codigo?: string | null; entrega_equipe_nome?: string | null;
  responsavel_recebimento: string | null;
  responsavel_recebimento_cargo?: string | null;
  /* A aprovação do relatório (0186). `aprovado` já vem resolvido pela
     visão: é ter aprovador OU ser lote do acervo, dispensado. */
  aprovado: boolean;
  aprovado_por: string | null;
  aprovado_por_nome: string | null;
  aprovado_por_cargo: string | null;
  aprovado_em: string | null;
  aprovacao_dispensada: boolean;
  /** Herança da 0012: descreviam o pacote inteiro. Só em registro antigo. */
  condicoes_embalagem?: string | null; possui_lacre?: boolean | null;
  divergencias: string | null; ressalvas: string | null;
  pdf_arquivo_id: string | null; pdf_revisao: number;
  pdf_emitido_em: string | null;
  /** Por onde as amostras são listadas — na ficha, no PDF e nas etiquetas (0185). */
  ordenacao_campo: 'codigo' | 'identificacao' | 'ponto';
  ordenacao_sentido: 'asc' | 'desc';
  amostras: Amostra[];
}

/** As abas da ficha do lote (258, §9.3 da ideia do fluxo da OS). */
type AbaDoLote = 'resumo' | 'amostras' | 'fotos' | 'documento';
const ABAS_DO_LOTE: { aba: AbaDoLote; nome: string }[] = [
  { aba: 'resumo', nome: 'Resumo' },
  { aba: 'amostras', nome: 'Amostras' },
  { aba: 'fotos', nome: 'Fotos' },
  { aba: 'documento', nome: 'Documento' },
];
const TOM_DA_DECISAO: Record<string, Tom> = {
  aceite: 'ok', aceite_com_ressalva: 'atencao', rejeicao: 'critico',
};

/** Um ensaio de uma amostra da OS — só o que a ficha do lote conta. */
interface EnsaioDaAmostra { amostra_id: string; status: string }

interface Evidencia {
  id: string; lote_id: string; amostra_id: string | null; arquivo_id: string;
  tipo: string; observacao: string | null; ordem: number;
  desconsiderada: boolean; motivo_desconsideracao: string | null;
  amostra_codigo: string | null;
  enviada_por_nome: string | null; enviada_por_email: string | null;
  capturada_em: string; dispositivo: string | null;
  nome_original: string; tamanho_bytes: string;
}

export function Recebimento() {
  const { id = '' } = useParams();
  const [parametros] = useSearchParams();
  const local = useLocation();
  const { pode } = useSessao();

  const estado = local.state as
    { recemCriado?: boolean; avisoFotos?: string } | null;

  const [lote, setLote] = useState<Lote | null>(null);
  useCaminho(lote ? [{ rotulo: '', codigo: lote.codigo }] : null);
  const [evidencias, setEvidencias] = useState<Evidencia[] | null>(null);
  /** Os ensaios das amostras da OS — para o próximo passo (258). */
  const [ensaios, setEnsaios] = useState<EnsaioDaAmostra[] | null>(null);
  const [verDesconsideradas, setVerDesconsideradas] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const amostraDestacada = parametros.get('amostra');
  /*
   * A aba vem do endereço (`?aba=`), para o link e o voltar do navegador.
   * O link de uma amostra (`?amostra=`) abre a aba das amostras.
   */
  const pedida = parametros.get('aba') as AbaDoLote | null;
  const aba: AbaDoLote = pedida && ABAS_DO_LOTE.some((a) => a.aba === pedida)
    ? pedida : amostraDestacada ? 'amostras' : 'resumo';
  /* As abas do lote grudam no celular como as da OS: tocada uma aba com
     elas grudadas, a aba nova abre logo abaixo delas (Ruling R33). */
  const faixaDoLote = useRef<HTMLElement>(null);
  const antesDeTrocarDeAba = useAbasQueGrudam(faixaDoLote, aba);

  const carregar = useCallback(async () => {
    try {
      const [l, e] = await Promise.all([
        api<Lote>(`/recebimento/${id}`),
        api<Evidencia[]>(`/recebimento/${id}/evidencias?todas=1`),
      ]);
      setLote(l); setEvidencias(e); setErro(null);
      if (l.os_id) {
        setEnsaios(await api<EnsaioDaAmostra[]>(`/os/${l.os_id}/ensaios-por-amostra`)
          .catch(() => null));
      } else setEnsaios(null);
    } catch (e) { setErro(e); }
  }, [id]);

  useEffect(() => { void carregar(); }, [carregar]);

  if (erro && !lote) return <ErroDaApi erro={erro} />;
  if (!lote || !evidencias) return <Carregando texto="Carregando recebimento" />;

  const visiveis = evidencias.filter((e) => verDesconsideradas || !e.desconsiderada);
  const fotosValidas = evidencias.filter((e) => !e.desconsiderada).length;

  /*
   * O próximo passo (258, §7 da ideia): «N amostras recebidas · X já com
   * ensaios · Y sem ensaio → Programar as Y». A amostra que chegou ligada a
   * uma prevista já traz os ensaios marcados nela; a que chegou sem previsão
   * espera a programação — e é para ela que o botão leva.
   */
  const comEnsaio = new Set((ensaios ?? [])
    .filter((e) => !['cancelado', 'substituido'].includes(e.status))
    .map((e) => e.amostra_id));
  const recebidas = lote.amostras.length;
  const jaComEnsaio = lote.amostras.filter((a) => comEnsaio.has(a.id)).length;
  const semEnsaio = recebidas - jaComEnsaio;
  const programarAs = lote.os_id && ensaios && semEnsaio > 0 && pode('os.programar')
    ? `/os/${lote.os_id}/programacao?recorte=sem_ensaio` : null;

  const proximoPasso = lote.os_id && ensaios && recebidas > 0 && (
    <div className="proximo-passo-lote" role="status">
      <p>
        <strong>{recebidas} amostra(s) recebida(s)</strong>
        {' · '}{jaComEnsaio} já com ensaios
        {' · '}{semEnsaio} sem ensaio
      </p>
      {programarAs ? (
        <Link className="botao" to={programarAs}>Programar as {semEnsaio}</Link>
      ) : (
        <Link className="botao secundario" to={`/os/${lote.os_id}/programacao`}>
          Ver a programação da OS
        </Link>
      )}
    </div>
  );

  return (
    <div className="ficha-lote">
      {/*
        * 258 · o recebimento no mesmo idioma da OS: o cabeçalho diz o que é
        * («Recebimento L2026-0024»), de qual OS (com o link) e de quem; a
        * ação principal é o próximo passo. Antes, o código do lote solto,
        * sem cliente e sem caminho de volta para a OS (U2, U9).
        */}
      <header className="cabeca-os cabeca-lote">
        <div className="cabeca-os-id">
          <div className="cabeca-os-linha">
            <h1>Recebimento <span className="mono">{lote.codigo}</span></h1>
            <Selo tom={TOM_DA_DECISAO[lote.decisao] ?? 'neutro'}>{rotulo(lote.decisao)}</Selo>
            {lote.status !== 'concluido' && <Selo tom="atencao">Rascunho</Selo>}
          </div>
          <p className="cabeca-os-sub">
            {lote.os_id
              ? <>OS <Link className="mono" to={`/os/${lote.os_id}`}>{lote.os_numero}</Link></>
              : <span>sem OS</span>}
            {lote.cliente && <> · {lote.cliente}</>}
            {(lote.obra_codigo || lote.obra_nome) && (
              <> · <span className="mono">{lote.obra_codigo}</span> {lote.obra_nome}</>
            )}
            {' · '}{dataHora(lote.datahora_recebimento)}
          </p>
        </div>
        <div className="cabeca-os-acoes">
          {/* 281: de volta à OS de onde se veio receber. */}
          {lote.os_id && (
            <Link className="botao secundario voltar-a-os" to={`/os/${lote.os_id}`}>
              Voltar à OS
            </Link>
          )}
          {programarAs && aba !== 'resumo' && (
            <Link className="botao" to={programarAs}>Programar as {semEnsaio}</Link>
          )}
          <ExcluirDeVez tipo="recebimento" id={lote.id} />
        </div>
      </header>

      <nav className="abas-os" aria-label="Abas do recebimento" ref={faixaDoLote}>
        {ABAS_DO_LOTE.map((a) => {
          const cont = a.aba === 'amostras' ? recebidas
            : a.aba === 'fotos' ? fotosValidas : undefined;
          const q = new URLSearchParams();
          if (a.aba !== 'resumo') q.set('aba', a.aba);
          return (
            <Link key={a.aba} replace
              to={{ search: q.toString() ? `?${q}` : '' }}
              className={`aba-os${a.aba === aba ? ' sel' : ''}`}
              aria-current={a.aba === aba ? 'page' : undefined}
              onClick={() => antesDeTrocarDeAba(a.aba)}>
              {a.nome}
              {cont !== undefined && <span className="aba-os-cont">{cont}</span>}
            </Link>
          );
        })}
      </nav>

      {estado?.recemCriado && (
        <Aviso tipo="ok" titulo="Recebimento registrado.">
          Imprima as etiquetas e cole nas amostras antes de guardá-las
          {' '}(aba <Link to="?aba=documento" replace>Documento</Link>).
        </Aviso>
      )}
      {estado?.avisoFotos && <Aviso tipo="atencao" titulo={estado.avisoFotos} />}
      <ErroDaApi erro={erro} />

      {aba === 'resumo' && (<>
        {/* 268: o escopo da OS na mesma faixa da OS e da Programação. */}
        {lote.os_id && <EscopoDaOs osId={lote.os_id} />}
        {proximoPasso}

        <Identificacao lote={lote} />

        {lote.status !== 'concluido' && (
          <Conclusao lote={lote} aoMudar={carregar} />
        )}

        {!lote.os_id && pode('recebimento.atribuir_os') && (
          <AtribuirOs lote={lote} aoMudar={carregar} />
        )}

        {/*
          * A OS chegou depois, e as amostras deste lote ainda não estão
          * ligadas ao que ela previu (onda 250, RN-219). O cartão só aparece
          * quando há o que ligar — e some sozinho depois.
          */}
        {lote.os_id && pode('amostra.receber') && (
          <LigarPrevistas osId={lote.os_id} loteId={lote.id} cartao aoLigar={carregar} />
        )}

        {lote.status === 'concluido' && !lote.aprovado && (
          <Aprovacao
            lote={lote}
            aoMudar={carregar}
            podeAprovar={pode('recebimento.aprovar')}
          />
        )}
      </>)}

      {aba === 'documento' && (
        <Acoes lote={lote} aoMudar={carregar} podeEmitir={pode('documento.emitir')} />
      )}

      {aba === 'amostras' && (
        <TabelaAmostras
          lote={lote}
          evidencias={evidencias}
          destacada={amostraDestacada}
          aoMudar={carregar}
          podeAcrescentar={pode('amostra.receber')}
          podeOrdenar={pode('amostra.receber')}
        />
      )}

      {aba === 'fotos' && (<>
        <div className="linha-topo">
          <h2 className="cresce">Registro fotográfico</h2>
          {evidencias.some((e) => e.desconsiderada) && (
            <button
              type="button" className="botao discreto"
              onClick={() => setVerDesconsideradas((v) => !v)}
              aria-pressed={verDesconsideradas}
            >
              {verDesconsideradas ? 'Ocultar desconsideradas' : 'Mostrar desconsideradas'}
            </button>
          )}
        </div>

        <Galeria
          lote={lote}
          evidencias={visiveis}
          todas={evidencias}
          soFaltando={parametros.get('filtro') === 'faltando'}
          podeDesconsiderar={pode('arquivo.desconsiderar')}
          aoMudar={carregar}
        />

        {pode('arquivo.enviar') && (
          <MaisFotos lote={lote} aoMudar={carregar} />
        )}
      </>)}

      {/* 269 (etapa D): a próxima ação do lote, fixa no rodapé. */}
      <BarraDoLote lote={lote} aba={aba} pode={pode} aoMudar={carregar} />
    </div>
  );
}

// ── A próxima ação do lote (269) ─────────────────────────────────────

/**
 * Uma ação só, a que destrava o lote — no rodapé, onde o dedo está.
 *
 * A ficha do lote tinha cinco botões empilhados em «Documento e
 * etiquetas» e a conclusão e a aprovação em cartões a meia página de
 * distância (achado 9 da ideia do recebimento no celular). A barra diz a
 * situação em uma frase e oferece o próximo gesto, na ordem do SGQ:
 * concluir (fotos obrigatórias) → aprovar → emitir o relatório → imprimir
 * as etiquetas. Quem não tem a permissão vê a frase, sem o botão.
 */
function BarraDoLote({ lote, aba, pode, aoMudar }: {
  lote: Lote; aba: AbaDoLote; pode(p: string): boolean; aoMudar(): Promise<void>;
}) {
  const navegar = useNavigate();
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const faltando = Number(lote.amostras_sem_minimo ?? 0);
  const irPara = (q: string, cartao?: string) => {
    navegar({ search: q }, { replace: true });
    if (cartao) {
      setTimeout(() => document.querySelector(`[data-cartao="${cartao}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
    }
  };
  async function concluir() {
    setErro(null); setOcupado(true);
    try {
      await api(`/recebimento/${lote.id}/concluir`, { metodo: 'POST' });
      await aoMudar();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  }

  let frase: string;
  let acao: { texto: string; fazer(): void } | null = null;
  if (lote.status !== 'concluido') {
    if (faltando > 0) {
      frase = `Rascunho · ${faltando} amostra(s) sem as fotos obrigatórias`;
      if (pode('arquivo.enviar')) {
        acao = { texto: 'Ver as que faltam', fazer: () => irPara('?aba=fotos&filtro=faltando') };
      }
    } else {
      frase = 'Rascunho · as fotos obrigatórias estão completas';
      if (pode('amostra.receber')) acao = { texto: 'Concluir o recebimento', fazer: () => void concluir() };
    }
  } else if (!lote.aprovado) {
    frase = 'Concluído · aguardando a aprovação do relatório';
    if (pode('recebimento.aprovar')) {
      acao = { texto: 'Aprovar o recebimento', fazer: () => irPara('', 'aprovacao') };
    }
  } else if (!lote.pdf_emitido_em) {
    frase = 'Aprovado · o relatório de recebimento ainda não foi emitido';
    if (pode('documento.emitir')) {
      acao = { texto: 'Emitir o relatório (PDF)', fazer: () => irPara('?aba=documento') };
    }
  } else {
    frase = `Relatório emitido em R${String(lote.pdf_revisao).padStart(2, '0')}`;
    acao = { texto: 'Imprimir as etiquetas', fazer: () => irPara('?aba=documento') };
  }
  /* Na aba onde a ação mora, o botão da barra seria eco do que está na tela. */
  const ecoa = acao && ((aba === 'documento' && /PDF|etiquetas/.test(acao.texto))
    || (aba === 'fotos' && /faltam/.test(acao.texto)));

  return (
    <div className="bloco confirmar confirmar-fixa barra-lote" role="region"
      aria-label="Próxima ação do recebimento">
      <p className="barra-lote-frase">{frase}</p>
      {erro ? <ErroDaApi erro={erro} /> : null}
      {acao && !ecoa && (
        <button type="button" className="botao" disabled={ocupado} onClick={acao.fazer}>
          {ocupado ? 'Concluindo…' : acao.texto}
        </button>
      )}
    </div>
  );
}

// ── Identificação ────────────────────────────────────────────────────

function Identificacao({ lote }: { lote: Lote }) {
  return (
    <div className="cartao">
      <table className="dados">
        <tbody>
          <tr><th style={{ width: '38%' }}>Cliente</th>
              <td>{lote.cliente ?? <span className="nada">sem cliente</span>}</td></tr>
          <tr><th>Obra</th>
              <td>{[lote.obra_codigo, lote.obra_nome].filter(Boolean).join(' — ')
                   || <span className="nada">—</span>}</td></tr>
          <tr><th>OS</th>
              <td>{lote.os_numero
                   ?? <span className="nada">sem OS — nenhum ensaio pode começar</span>}</td></tr>
          <tr><th>Situação</th>
              <td>{lote.status === 'concluido'
                   ? 'Recebimento concluído'
                   : <b>Rascunho — ainda não concluído</b>}</td></tr>
          <tr><th>Data/hora</th><td>{dataHora(lote.datahora_recebimento)}</td></tr>
          <tr><th>Origem</th><td>{rotulo(lote.origem)}</td></tr>
          <tr><th>Entregue por</th>
              <td>{lote.entrega_equipe_id
                    ? `Equipe ${lote.entrega_equipe_codigo} — ${lote.entrega_equipe_nome}`
                    : ([lote.responsavel_entrega_nome, lote.responsavel_entrega_empresa]
                        .filter(Boolean).join(' — ')
                       || <span className="nada">—</span>)}
                  {lote.responsavel_entrega_doc && ` — doc. ${lote.responsavel_entrega_doc}`}</td></tr>
          <tr><th>Recebido por</th>
              <td>{lote.responsavel_recebimento
                    ? <>{lote.responsavel_recebimento}
                        {lote.responsavel_recebimento_cargo
                          && <span className="det"> · {lote.responsavel_recebimento_cargo}</span>}</>
                    : <span className="nada">—</span>}</td></tr>
          <tr><th>Aprovação</th>
              <td>{lote.aprovado_por
                    ? <>Aprovado por <b>{lote.aprovado_por_nome}</b>
                        {lote.aprovado_por_cargo && ` · ${lote.aprovado_por_cargo}`}
                        {' '}em {dataHora(lote.aprovado_em)}</>
                    : lote.aprovacao_dispensada
                      ? <span className="nada">
                          recebimento anterior à regra de aprovação
                        </span>
                      : <b>Aguardando aprovação</b>}</td></tr>
          {/* Herança: só aparece em recebimento anterior à 0012, quando a
              condição era do pacote. Hoje é de cada amostra, na tabela abaixo. */}
          {lote.condicoes_embalagem && (
            <tr><th>Condições da embalagem</th><td>{lote.condicoes_embalagem}</td></tr>
          )}
          {lote.possui_lacre !== null && lote.possui_lacre !== undefined && (
            <tr><th>Lacre</th>
                <td>{lote.possui_lacre ? 'Presente e íntegro' : 'Ausente'}</td></tr>
          )}
          <tr><th>Divergências</th>
              <td>{lote.divergencias ?? <span className="nada">nenhuma registrada</span>}</td></tr>
          <tr><th>Ressalvas</th>
              <td>{lote.ressalvas ?? <span className="nada">nenhuma registrada</span>}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Conclusão do recebimento ─────────────────────────────────────────

/**
 * O recebimento em rascunho.
 *
 * Existe porque as fotos sobem depois do lote (migração `0012`): se a
 * conexão cai no meio do envio, o recebimento fica gravado e incompleto.
 * Sem este cartão, ninguém saberia — e o documento sairia sem a evidência.
 *
 * Quem confere o mínimo é o banco. A tela mostra a contagem e deixa
 * tentar; a recusa vem com os códigos das amostras que faltam, e é essa
 * frase que aparece aqui.
 */
function Conclusao({ lote, aoMudar }: { lote: Lote; aoMudar(): Promise<void> }) {
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const faltando = Number(lote.amostras_sem_minimo ?? 0);

  async function concluir() {
    setErro(null); setOcupado(true);
    try {
      await api(`/recebimento/${lote.id}/concluir`, { metodo: 'POST' });
      await aoMudar();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  }

  return (
    <div className="cartao" data-cartao="conclusao">
      <h2>Recebimento em rascunho</h2>
      <ErroDaApi erro={erro} />
      <p className="sub">
        {faltando > 0
          ? <>
              {faltando === 1 ? 'Uma amostra está' : `${faltando} amostras estão`} sem as
              duas fotos obrigatórias — <b>embalagem externa</b> e{' '}
              <b>etiqueta de identificação</b>. Anexe abaixo e conclua.
            </>
          : <>Todas as amostras têm as fotos obrigatórias. Conclua para liberar o
              relatório de recebimento.</>}
      </p>
      <button
        type="button" className="botao"
        style={{ marginTop: '.6rem' }}
        disabled={ocupado}
        onClick={concluir}
      >
        {ocupado ? 'Concluindo…' : 'Concluir recebimento'}
      </button>
    </div>
  );
}

// ── Aprovação do relatório ───────────────────────────────────────────

/**
 * A aprovação do relatório de recebimento (0186).
 *
 * «O relatório de recebimento deve ser verificado e aprovado também,
 *  indo para os campos no rodapé do relatório» — Humberto, 21/09/2026.
 *
 * Duas assinaturas, não três: a Testesolo decidiu que a conferência do
 * recebimento É a aprovação. E quem recebeu pode aprovar, se tiver a
 * permissão — o recebimento não é resultado de ensaio, e a segregação da
 * `RN-124` pararia a bancada de um laboratório de dez pessoas.
 *
 * O cartão só existe enquanto falta aprovar. Quem não tem a permissão vê
 * o mesmo cartão SEM o botão, e com o nome de quem pode: procurar quem
 * aprova é a ação que sobra para ele, e a tela que esconde o cartão
 * esconde também essa informação.
 */
function Aprovacao({ lote, aoMudar, podeAprovar }: {
  lote: Lote; aoMudar(): Promise<void>; podeAprovar: boolean;
}) {
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const [observacao, setObservacao] = useState('');

  async function aprovar() {
    setErro(null); setOcupado(true);
    try {
      await api(`/recebimento/${lote.id}/aprovar`, {
        corpo: observacao.trim() ? { observacao: observacao.trim() } : {},
      });
      await aoMudar();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  }

  return (
    <div className="cartao" data-cartao="aprovacao">
      <h2>Aguardando aprovação</h2>
      <ErroDaApi erro={erro} />
      <p className="sub">
        O relatório está conferido e fechado, mas ainda não aprovado. Até a
        aprovação sai só a <b>pré-visualização</b>, com a tarja PRELIMINAR —
        o laboratório pode ensaiar as amostras normalmente.
      </p>
      {podeAprovar
        ? (
          <>
            <Campo
              rotulo="Observação da aprovação"
              value={observacao}
              maxLength={2000}
              autoComplete="off"
              dica="Opcional. Vai para a trilha de auditoria, não para o documento."
              onChange={(e) => setObservacao(e.target.value)}
            />
            <button
              type="button" className="botao"
              style={{ marginTop: '.6rem' }}
              disabled={ocupado}
              onClick={aprovar}
            >
              {ocupado ? 'Aprovando…' : 'Aprovar relatório de recebimento'}
            </button>
          </>
        )
        : (
          <p className="sub">
            Seu perfil não aprova recebimento. Quem aprova é o gestor do
            laboratório, o responsável técnico ou a administração.
          </p>
        )}
    </div>
  );
}

// ── A OS que chega depois ────────────────────────────────────────────

/**
 * Atribuir a OS a um recebimento órfão.
 *
 * O cliente mandou material sem avisar; o laboratório recebeu, porque o
 * material já está lá. Quando a OS aparece, ela desce para o lote e para
 * todas as amostras — subdivisões inclusive. Antes disso, nenhum ensaio
 * pode começar.
 */
function AtribuirOs({ lote, aoMudar }: { lote: Lote; aoMudar(): Promise<void> }) {
  const [texto, setTexto] = useState('');
  const [achados, setAchados] = useState<Array<{
    id: string; numero: string; obra_nome: string | null; cliente: string | null;
  }> | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    const alvo = texto.trim();
    if (alvo.length < 2) { setAchados(null); return; }
    const abortar = new AbortController();
    const t = setTimeout(() => {
      api<Array<{ id: string; numero: string; obra_nome: string | null; cliente: string | null }>>(
        `/os?busca=${encodeURIComponent(alvo)}`, { sinal: abortar.signal })
        .then((r) => setAchados(r.slice(0, 8)))
        .catch((e) => { if ((e as Error).name !== 'AbortError') setAchados([]); });
    }, 300);
    return () => { clearTimeout(t); abortar.abort(); };
  }, [texto]);

  async function atribuir(osId: string) {
    setErro(null); setOcupado(true);
    try {
      await api(`/recebimento/${lote.id}/os`, { metodo: 'PATCH', corpo: { osId } });
      await aoMudar();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  }

  return (
    <div className="cartao">
      <h2>Este recebimento está sem OS</h2>
      <ErroDaApi erro={erro} />
      <p className="sub">
        As amostras estão rastreadas, mas <b>nenhum ensaio pode começar</b> até a OS
        ser atribuída. A OS desce para o lote e para todas as amostras dele — e, se
        ela previu amostras, a ficha oferece em seguida ligar cada uma à sua prevista.
      </p>
      <div className="campo" style={{ marginTop: '.6rem' }}>
        <input
          type="search"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Número da obra (7 dígitos), obra ou cliente"
          inputMode="numeric"
          autoComplete="off"
          aria-label="Buscar ordem de serviço"
        />
        {achados && achados.length > 0 && (
          <div className="lista" style={{ marginTop: '.5rem' }}>
            {achados.map((o) => (
              <button
                key={o.id} type="button" className="item"
                disabled={ocupado}
                onClick={() => atribuir(o.id)}
              >
                <span className="cod">{o.numero}</span>
                <span className="sub">{o.obra_nome}</span>
                <span className="meta">{o.cliente}</span>
              </button>
            ))}
          </div>
        )}
        {achados && achados.length === 0 && texto.trim().length >= 2 && (
          <span className="dica">Nenhuma OS encontrada para “{texto.trim()}”.</span>
        )}
      </div>
    </div>
  );
}

// ── Ações: documento, PDF, etiquetas ─────────────────────────────────

function Acoes({
  lote, aoMudar, podeEmitir,
}: { lote: Lote; aoMudar(): Promise<void>; podeEmitir: boolean }) {
  const [html, setHtml] = useState<{ titulo: string; corpo: string } | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [pedindoMotivo, setPedindoMotivo] = useState(false);
  const [motivo, setMotivo] = useState('');

  async function abrirHtml(caminho: string, titulo: string) {
    setErro(null); setOcupado(titulo);
    try {
      setHtml({ titulo, corpo: await api<string>(caminho, { cru: true }) });
    } catch (e) { setErro(e); } finally { setOcupado(null); }
  }

  async function emitir(comMotivo?: string) {
    setErro(null); setOcupado('PDF');
    try {
      const r = await api<{ arquivo_id: string; nome: string; revisao: number }>(
        `/recebimento/${lote.id}/pdf`,
        { corpo: comMotivo ? { motivo: comMotivo } : {} });
      setPedindoMotivo(false); setMotivo('');
      await aoMudar();
      await baixar(r.arquivo_id, r.nome);
    } catch (e) {
      // A API exige motivo a partir da segunda emissão: a segunda via de um
      // documento do SGQ é um evento, não um clique repetido.
      if (e instanceof ErroApi && e.campos.some((c) => c.campo === 'motivo')) {
        setPedindoMotivo(true);
      } else {
        setErro(e);
      }
    } finally { setOcupado(null); }
  }

  async function baixar(arquivoId: string, nome: string) {
    const blob = await baixarBlob(`/arquivos/${arquivoId}/conteudo?download=1`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = nome;
    document.body.appendChild(a); a.click(); a.remove();
    // Um instante para o navegador começar a gravar antes de soltar os bytes.
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }

  return (
    <div className="cartao">
      <h2>Documento e etiquetas</h2>
      <ErroDaApi erro={erro} />

      {lote.pdf_revisao > 0 && (
        <p className="sub">
          PDF emitido na revisão R{String(lote.pdf_revisao).padStart(2, '0')} em{' '}
          {dataHora(lote.pdf_emitido_em)}. Reemitir <b>não</b> apaga o anterior:
          sobe a revisão e grava um arquivo novo.
        </p>
      )}

      {/*
        * 287 (RN-232): o lote sem OS também imprime — o QR não espera —, mas
        * a etiqueta sai sem a OS e sem os ensaios. Quem imprime fica sabendo
        * que ela vai ficar velha, e que a OS avisa quando ficar.
        */}
      {!lote.os_id && lote.amostras.length > 0 && (
        <Aviso tipo="atencao" titulo="Este lote ainda não tem OS.">
          A etiqueta sai sem o número da OS e sem os ensaios — mas sai: é o QR
          que identifica a amostra na prateleira. Depois de vincular a OS e
          programar, a OS avisa «reimprimir».
        </Aviso>
      )}
      {/* 258: botões do Carbon no lugar dos chips (U1, U12). */}
      <div className="escolha acoes-documento" style={{ marginTop: '.5rem' }}>
        <button
          type="button"
          onClick={() => abrirHtml(`/recebimento/${lote.id}/documento`, 'Pré-visualização')}
          disabled={!!ocupado}
        >
          {ocupado === 'Pré-visualização' ? 'Abrindo…' : 'Pré-visualizar relatório'}
        </button>
        <button
          type="button"
          onClick={() => abrirHtml(`/amostras/etiquetas?lote=${lote.id}&registrar=recebimento`, 'Etiquetas')}
          disabled={!!ocupado || lote.amostras.length === 0}
        >
          {ocupado === 'Etiquetas' ? 'Abrindo…' : 'Folha de etiquetas'}
        </button>
        {/*
          * O rolo térmico, 100 × 50 mm na Zebra (onda 253 — era 50 × 30)
          * — o mesmo lote, o mesmo QR e o mesmo desenho da folha A4.
          *
          * Fica aqui, no recebimento, porque é aqui que a etiqueta é
          * colada: a amostra chega, é conferida e vai para a prateleira
          * identificada. Imprimir depois é ir buscar a caixa de novo.
          */}
        {/*
          * Este sai em PDF, e os outros em HTML — de propósito.
          *
          * A pré-visualização e a folha A4 são para OLHAR na tela, e
          * HTML dentro do quadro é o jeito de olhar. A etiqueta de rolo
          * é para IMPRIMIR, e HTML entrega o tamanho final à caixa de
          * impressão do navegador, cujo padrão é «Ajustar à página» —
          * 100 mm viram 94 e a margem aparece de um lado só. O PDF de
          * 100 × 50 mm não passa por esse diálogo.
          */}
        {/*
          * Sem `className="botao"` de propósito: dentro de `.escolha` os
          * irmãos são botões-chip, e `.escolha button` ganhava o fundo
          * do `.botao` mas não a tinta — sobrava texto claro sobre fundo
          * claro («sem contraste e fora de padrão», 04/09/2026). Vazio,
          * ele veste o mesmo chip dos vizinhos.
          */}
        <LinkProtegido
          className=""
          caminho={`/amostras/etiquetas?lote=${lote.id}&formato=rolo&pdf=1&registrar=recebimento`}
          nomeQueda={`etiquetas-100x50-${lote.id.slice(0, 8)}.pdf`}
        >
          Etiquetas 100 × 50 mm
        </LinkProtegido>
        {podeEmitir && (
          /*
           * Desabilitado, não escondido, enquanto o lote é rascunho.
           *
           * Esconder o botão faria o técnico procurar por ele. Deixá-lo
           * ativo faria o servidor recusar depois de uma viagem de rede. O
           * meio-termo diz o que falta e onde: o cartão de conclusão está
           * logo acima, com os nomes das amostras sem foto.
           */
          <button
            type="button" className="emitir-documento"
            onClick={() => (lote.pdf_revisao > 0 ? setPedindoMotivo(true) : emitir())}
            disabled={!!ocupado || lote.status !== 'concluido' || !lote.aprovado}
            /*
             * Desabilitado com o motivo à mão (0186). O botão que some
             * faz procurar; o botão que recusa depois da viagem de rede
             * faz esperar para ler «não».
             */
            title={lote.status !== 'concluido'
              ? 'Conclua o recebimento antes de emitir o relatório.'
              : !lote.aprovado
                ? 'Aprove o relatório de recebimento antes de emitir: sem '
                  + 'aprovação sai só a pré-visualização, com a tarja PRELIMINAR.'
                : undefined}
          >
            {/* "Deveria ser Emitir Relatório (PDF)" (Testesolo,
                23/08/2026): o que se emite é o RELATÓRIO de recebimento;
                PDF é só o formato em que ele sai. */}
            {ocupado === 'PDF' ? 'Gerando…'
              : lote.pdf_revisao > 0
                ? 'Reemitir relatório (PDF)' : 'Emitir relatório (PDF)'}
          </button>
        )}
        {lote.pdf_arquivo_id && (
          <button
            type="button"
            onClick={() => baixar(
              lote.pdf_arquivo_id!,
              `${lote.codigo}-recebimento-R${String(lote.pdf_revisao).padStart(2, '0')}.pdf`)}
          >
            Baixar o PDF emitido
          </button>
        )}
      </div>

      <Dialogo
        aberto={pedindoMotivo}
        titulo="Reemitir o relatório"
        aoFechar={() => setPedindoMotivo(false)}
      >
        <p className="sub">
          Este recebimento já tem documento emitido. A reemissão gera a revisão
          R{String(lote.pdf_revisao + 1).padStart(2, '0')} e fica na trilha com o motivo.
        </p>
        <Campo
          rotulo="Por que está reemitindo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="ex.: fotografia do tubo SP-04 anexada depois da primeira emissão"
        />
        <div className="linha">
          <button
            type="button" className="botao"
            disabled={motivo.trim().length < 5 || !!ocupado}
            onClick={() => emitir(motivo.trim())}
          >
            {ocupado === 'PDF' ? 'Gerando…' : 'Reemitir'}
          </button>
          <button
            type="button" className="botao secundario"
            onClick={() => setPedindoMotivo(false)}
          >
            Cancelar
          </button>
        </div>
      </Dialogo>

      {html && <JanelaDocumento titulo={html.titulo} corpo={html.corpo} aoFechar={() => setHtml(null)} />}
    </div>
  );
}

// ── Amostras ─────────────────────────────────────────────────────────

/** O nome da coluna por extenso, para a frase embaixo do cabeçalho. */
const ROTULO_ORDEM: Record<string, string> = {
  codigo: 'Amostra',
  identificacao: 'Identificação',
  ponto: 'Furo/Ponto',
};

function TabelaAmostras({
  lote, evidencias, destacada, aoMudar, podeAcrescentar, podeOrdenar,
}: {
  lote: Lote; evidencias: Evidencia[]; destacada: string | null;
  aoMudar(): Promise<void>; podeAcrescentar: boolean; podeOrdenar: boolean;
}) {
  const [acrescentando, setAcrescentando] = useState(false);
  const [ordenando, setOrdenando] = useState(false);
  const [erroOrdem, setErroOrdem] = useState<unknown>(null);

  const ordem = lote.ordenacao_campo ?? 'identificacao';
  const sentido = lote.ordenacao_sentido ?? 'asc';

  /*
   * A ordem é do LOTE, não desta tela (0185).
   *
   * Por isso o clique vai ao servidor em vez de reordenar o vetor aqui:
   * «isso irá refletir na sequência de amostras do relatório de
   * recebimento e na sequência das etiquetas» (Humberto, 21/09/2026).
   * Reordenar só na tela deixaria o papel numa ordem e a tela noutra —
   * que é exatamente o contrário do que foi pedido.
   */
  async function trocarOrdem(campo: string) {
    if (ordenando) return;
    const novo = campo === ordem && sentido === 'asc' ? 'desc' : 'asc';
    setErroOrdem(null); setOrdenando(true);
    try {
      await api(`/recebimento/${lote.id}/ordenacao`,
        { metodo: 'PATCH', corpo: { campo, sentido: novo } });
      await aoMudar();
    } catch (e) { setErroOrdem(e); } finally { setOrdenando(false); }
  }
  const porAmostra = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const e of evidencias) {
      if (!e.amostra_id || e.desconsiderada) continue;
      const s = m.get(e.amostra_id) ?? new Set<string>();
      s.add(e.tipo);
      m.set(e.amostra_id, s);
    }
    return m;
  }, [evidencias]);

  const botao = podeAcrescentar && (
    <button
      type="button" className="botao discreto"
      onClick={() => setAcrescentando(true)}
    >
      Acrescentar amostra
    </button>
  );

  const janela = acrescentando && (
    <FormMaisAmostra
      lote={lote}
      aoFechar={() => setAcrescentando(false)}
      aoPronto={async () => { setAcrescentando(false); await aoMudar(); }}
    />
  );

  if (!lote.amostras.length) {
    return (
      <>
        <Vazio>Nenhuma amostra neste recebimento.</Vazio>
        {botao}
        {janela}
      </>
    );
  }

  return (
    <>
      {janela}
      <ErroDaApi erro={erroOrdem} />
      {/*
        * A tabela inteira é do `QuadroDeAmostras` (onda 196) — a mesma
        * que a ficha da OS usa. «Isso pode ser editado em Amostras ou em
        * OS» (Humberto, 21/09/2026): uma tabela em dois lugares, e não
        * duas tabelas parecidas que amanhã divergem.
        *
        * O que é DESTE lugar vai por propriedade: a coluna de fotos, os
        * cabeçalhos que gravam a ordem do lote (194) e o botão de
        * acrescentar amostra.
        */}
      <QuadroDeAmostras
        amostras={lote.amostras}
        podeEditar={podeOrdenar}
        aoSalvo={aoMudar}
        lixeira
        destacada={destacada}
        titulo={plural(lote.amostras.length, 'amostra recebida', 'amostras recebidas')}
        acoes={botao}
        ordenacao={podeOrdenar
          ? { ordem, sentido, aoTrocar: trocarOrdem } : undefined}
        colunaFotos={{
          /* "Fotos" logo depois do código, e não no fim: é a única
             coluna acionável — a que diz de qual amostra ainda falta
             evidência. Deixá-la no fim de uma tabela que rola de lado
             é escondê-la. */
          titulo: 'Fotos',
          celula: (a) => {
            const tipos = porAmostra.get(a.id) ?? new Set<string>();
            const feitos = ROTEIRO.filter((t) => tipos.has(t)).length;
            return (
              <span className={`selo ${feitos === ROTEIRO.length ? 'aceite' : 'aceite_com_ressalva'}`}>
                {feitos}/{ROTEIRO.length}
              </span>
            );
          },
        }}
        nota={podeOrdenar && (
          /*
           * A frase existe porque a consequência do clique não está na
           * tela onde ele acontece. Quem ordena aqui está mudando o
           * documento e a folha de etiquetas — e só descobriria isso ao
           * emitir. Dizer antes custa uma linha.
           */
          <p className="sub" style={{ marginTop: 0 }}>
            Ordenado por <b>{ROTULO_ORDEM[ordem]}</b>,{' '}
            {sentido === 'asc' ? 'crescente' : 'decrescente'}
            {ordenando && ' — gravando…'}. Esta é a ordem em que as amostras
            saem no <b>relatório de recebimento</b> e nas <b>etiquetas</b>.
          </p>
        )}
      />
    </>
  );
}

// ── Galeria ──────────────────────────────────────────────────────────

function Galeria({
  lote, evidencias, todas, soFaltando, podeDesconsiderar, aoMudar,
}: {
  lote: Lote; evidencias: Evidencia[];
  /** 269: todas as evidências, para saber o que falta (as desconsideradas não contam). */
  todas: Evidencia[];
  /** 269: o filtro «faltando» — só as amostras sem as fotos obrigatórias. */
  soFaltando: boolean;
  podeDesconsiderar: boolean; aoMudar(): Promise<void>;
}) {
  const [alvo, setAlvo] = useState<Evidencia | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  async function desconsiderar() {
    if (!alvo) return;
    setErro(null); setEnviando(true);
    try {
      await api(`/recebimento/${lote.id}/evidencias/${alvo.id}/desconsiderar`,
        { corpo: { motivo: motivo.trim() } });
      setAlvo(null); setMotivo('');
      await aoMudar();
    } catch (e) { setErro(e); } finally { setEnviando(false); }
  }

  /*
   * 269 (etapa D): o que falta de cada amostra — as fotos obrigatórias
   * (`MINIMO`) que ainda não há, sem contar as desconsideradas. O filtro
   * «faltando» mostra só essas amostras, mesmo as que não têm foto
   * nenhuma; é a lista de trabalho de quem volta ao lote para completar.
   */
  const faltaDe = (amostraId: string) => MINIMO.filter((t) =>
    !todas.some((e) => e.amostra_id === amostraId && e.tipo === t && !e.desconsiderada));
  const comFalta = lote.amostras.filter((a) => faltaDe(a.id).length > 0);

  const filtro = (
    <div className="filtro-fotos seg" role="group" aria-label="Quais amostras">
      <Link replace to="?aba=fotos" aria-pressed={!soFaltando}
        className={!soFaltando ? 'sel' : undefined}>
        Todas <span className="n mono">{lote.amostras.length}</span>
      </Link>
      <Link replace to="?aba=fotos&filtro=faltando" aria-pressed={soFaltando}
        className={soFaltando ? 'sel' : undefined}>
        Faltando <span className="n mono">{comFalta.length}</span>
      </Link>
    </div>
  );

  if (!evidencias.length && !soFaltando) {
    return <>{filtro}<Vazio>Nenhuma fotografia neste recebimento.</Vazio></>;
  }

  const grupos: Array<{ chave: string; titulo: string; itens: Evidencia[]; falta: string[] }> = [];
  for (const a of (soFaltando ? comFalta : lote.amostras)) {
    const itens = evidencias.filter((e) => e.amostra_id === a.id);
    const falta = faltaDe(a.id);
    if (itens.length || soFaltando) {
      grupos.push({
        chave: a.id,
        titulo: nomeDaAmostra({ codigo: a.codigo, identificacao: a.identificacao_recebida }),
        itens, falta,
      });
    }
  }
  const soltas = evidencias.filter(
    (e) => !e.amostra_id || !lote.amostras.some((a) => a.id === e.amostra_id));
  if (soltas.length && !soFaltando) grupos.push({ chave: 'lote', titulo: 'Do lote', itens: soltas, falta: [] });

  return (
    <>
      {filtro}
      <ErroDaApi erro={erro} />
      {soFaltando && !grupos.length && (
        <Vazio>Todas as amostras têm as fotos obrigatórias.</Vazio>
      )}
      {grupos.map((g) => (
        <div className="cartao grupo-fotos" key={g.chave}>
          <h3 style={{ marginBottom: '.6rem' }}>
            {g.titulo}
            {g.falta.length > 0 && (
              <span className="selo atencao falta-foto">
                Falta {g.falta.map((t) => rotulo(t).toLowerCase()).join(' e ')}
              </span>
            )}
          </h3>
          {/* 269: miniaturas, quatro por linha — a foto inteira abre na nova aba. */}
          <div className="grade-fotos miniaturas">
            {g.itens.map((e) => (
              <div className={`foto${e.desconsiderada ? ' fora' : ''}`} key={e.id}>
                <FotoProtegida arquivoId={e.arquivo_id} alt={`Fotografia: ${rotulo(e.tipo)}`} />
                <div className="leg">
                  <b>{rotulo(e.tipo)}</b>
                  {e.observacao && <span>{e.observacao}</span>}
                  <span>
                    {dataHora(e.capturada_em)}
                    {e.enviada_por_nome && ` · ${e.enviada_por_nome}`}
                  </span>
                  {e.desconsiderada
                    ? (
                      <span className="fora-tag">
                        Desconsiderada — {e.motivo_desconsideracao}
                      </span>
                    )
                    : podeDesconsiderar && (
                      <button
                        type="button" className="botao discreto"
                        style={{ minHeight: 34, fontSize: '.78rem', paddingLeft: 0 }}
                        onClick={() => { setAlvo(e); setMotivo(''); }}
                      >
                        Desconsiderar
                      </button>
                    )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialogo
        aberto={!!alvo}
        titulo="Tirar a foto do documento"
        aoFechar={() => setAlvo(null)}
      >
        <p className="sub">
          A foto <b>não</b> é apagada: ela sai do relatório e continua auditável,
          com o motivo, quem tirou e quando. É o que o SGQ exige.
        </p>
        <Campo
          rotulo="Por que esta foto sai do documento"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="ex.: anexada ao lote errado"
        />
        <div className="linha">
          <button
            type="button" className="botao perigo"
            disabled={motivo.trim().length < 5 || enviando}
            onClick={desconsiderar}
          >
            {enviando ? 'Registrando…' : 'Desconsiderar'}
          </button>
          <button type="button" className="botao secundario" onClick={() => setAlvo(null)}>
            Cancelar
          </button>
        </div>
      </Dialogo>
    </>
  );
}

// ── Anexar mais fotos ────────────────────────────────────────────────

function MaisFotos({ lote, aoMudar }: { lote: Lote; aoMudar(): Promise<void> }) {
  const [fotos, setFotos] = useState<FotoPendente[]>([]);
  const [aberto, setAberto] = useState(false);
  const [progresso, setProgresso] = useState<number | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  const amostras = lote.amostras.map((a) => ({
    rotulo: nomeDaAmostra({ codigo: a.codigo, identificacao: a.identificacao_recebida }),
  }));

  async function enviar() {
    setErro(null); setProgresso(0);
    try {
      await enviarFotos(
        lote.id, ordenarParaEnvio(fotos), lote.amostras.map((a) => a.id), setProgresso);
      descartarPrevias(fotos);
      setFotos([]); setAberto(false);
      await aoMudar();
    } catch (e) { setErro(e); } finally { setProgresso(null); }
  }

  if (!aberto) {
    return (
      <button
        type="button" className="botao secundario largo grande"
        onClick={() => setAberto(true)}
      >
        + Anexar fotos
      </button>
    );
  }

  return (
    <div>
      <div className="linha-topo">
        <h2 className="cresce">Anexar fotos</h2>
        <button
          type="button" className="botao discreto"
          onClick={() => { descartarPrevias(fotos); setFotos([]); setAberto(false); }}
        >
          Cancelar
        </button>
      </div>

      <ErroDaApi erro={erro} />

      {lote.pdf_revisao > 0 && (
        <Aviso tipo="atencao" titulo="Este recebimento já tem documento emitido.">
          Anexar foto agora não altera o PDF que já saiu. Para o cliente receber a
          evidência nova, reemita — o que gera a revisão
          R{String(lote.pdf_revisao + 1).padStart(2, '0')} com motivo.
        </Aviso>
      )}

      <RoteiroFotos
        fotos={fotos} aoMudar={setFotos} amostras={amostras}
        desabilitado={progresso !== null}
      />

      {progresso !== null
        ? (
          <div className="cartao" role="status">
            <p>Enviando {plural(fotos.length, 'foto', 'fotos')}…</p>
            <Progresso fracao={progresso} texto="Enviando fotografias" />
          </div>
        )
        : (
          <button
            type="button" className="botao largo grande"
            disabled={!fotos.length}
            onClick={enviar}
          >
            Enviar {fotos.length ? plural(fotos.length, 'foto', 'fotos') : 'fotos'}
          </button>
        )}
    </div>
  );
}

// ── A caixa que ficou no carro ────────────────────────────────────────

const OPCOES_TIPO_AMOSTRA: Opcao[] = TIPOS_AMOSTRA.map(
  (v) => ({ valor: v, texto: rotulo(v) }));

/**
 * A amostra que chegou depois, no mesmo lote.
 *
 * Acontece: a caixa que ficou no carro, o tubo que estava em outra van. A
 * rota existe desde a S2 e exige motivo — porque acrescentar amostra a um
 * recebimento **já assinado** muda um documento do SGQ, e a trilha tem que
 * dizer por quê. O que faltava era a ficha oferecer o caminho; sem ele, a
 * saída do técnico era abrir um segundo recebimento para o mesmo material,
 * e aí o lote deixa de descrever a entrega.
 *
 * Uma amostra por vez, de propósito. O assistente do recebimento repete
 * blocos porque lá se lança a carga inteira de uma vez; aqui é o caso
 * excepcional, quase sempre de uma ou duas peças, e uma janela com N
 * blocos repetidos custaria mais atenção do que economiza.
 *
 * O PDF já emitido NÃO é alterado aqui: emitir de novo é ato à parte, e a
 * tela diz isso antes do clique em vez de deixar o técnico descobrir que o
 * documento do cliente ficou desatualizado.
 */
function FormMaisAmostra({
  lote, aoFechar, aoPronto,
}: { lote: Lote; aoFechar(): void; aoPronto(): Promise<void> }) {
  const [identificacao, setIdentificacao] = useState('');
  const [tipo, setTipo] = useState<string | null>(null);
  const [acond, setAcond] = useState<string | null>(null);
  const [descricao, setDescricao] = useState('');
  const [ponto, setPonto] = useState('');
  const [pi, setPi] = useState('');
  const [pf, setPf] = useState('');
  const [coleta, setColeta] = useState('');
  const [local, setLocal] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const opcoesAcond: Opcao[] = (tipo ? ACONDICIONAMENTO[tipo] ?? [] : [])
    .map((v) => ({ valor: v, texto: rotulo(v) }));

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    try {
      await api(`/recebimento/${lote.id}/amostras`, {
        metodo: 'POST',
        corpo: {
          motivo: motivo.trim(),
          amostras: [{
            identificacaoRecebida: identificacao.trim() || undefined,
            tipoAmostra: tipo ?? undefined,
            tipoAcondicionamento: acond ?? undefined,
            acondicionamentoDescricao: descricao.trim() || undefined,
            pontoInvestigacao: ponto.trim() || undefined,
            profundidadeInicial: paraNumero(pi),
            profundidadeFinal: paraNumero(pf),
            dataColeta: coleta || undefined,
            localizacaoFisica: local.trim() || undefined,
          }],
        },
      });
      await aoPronto();
    } catch (x) {
      setErro(x);
    } finally {
      setEnviando(false);
    }
  };

  const doCampo = (campo: string) =>
    (erro instanceof ErroApi ? erro.campo(campo) : undefined);

  return (
    <Dialogo aberto titulo="Acrescentar amostra ao recebimento" aoFechar={aoFechar}>
      <form onSubmit={enviar}>
        <p className="sub">
          {lote.codigo}
          {lote.cliente && ` · ${lote.cliente}`}
        </p>

        <ErroDaApi erro={erro} />

        <Campo
          rotulo="Identificação que veio com a amostra"
          value={identificacao}
          onChange={(e) => setIdentificacao(e.currentTarget.value)}
          erro={doCampo('identificacaoRecebida')}
          dica="O que está escrito no saco, no tubo ou na etiqueta do cliente."
          autoFocus
        />

        <Escolha
          rotulo="Tipo"
          opcoes={OPCOES_TIPO_AMOSTRA}
          valor={tipo}
          aoEscolher={(v) => { setTipo(v); setAcond(null); }}
          erro={doCampo('tipoAmostra')}
        />

        {tipo && (
          <Escolha
            rotulo={rotuloDoAcondicionamento(tipo)}
            opcoes={opcoesAcond}
            valor={acond}
            aoEscolher={setAcond}
            erro={doCampo('tipoAcondicionamento')}
          />
        )}

        {acond === 'outro' && (
          <Campo
            rotulo="Diga o que é"
            value={descricao}
            onChange={(e) => setDescricao(e.currentTarget.value)}
            erro={doCampo('acondicionamentoDescricao')}
            dica="Tubo de PVC, cilindro de CBR, lascas, balde."
            required
          />
        )}

        <Campo
          rotulo="Furo ou ponto"
          value={ponto}
          onChange={(e) => setPonto(e.currentTarget.value)}
          erro={doCampo('pontoInvestigacao')}
        />

        <div className="linha">
          <Campo
            rotulo="Profundidade inicial (m)"
            value={pi}
            onChange={(e) => setPi(e.currentTarget.value)}
            erro={doCampo('profundidadeInicial')}
            inputMode="decimal"
          />
          <Campo
            rotulo="Profundidade final (m)"
            value={pf}
            onChange={(e) => setPf(e.currentTarget.value)}
            erro={doCampo('profundidadeFinal')}
            inputMode="decimal"
          />
        </div>

        <Campo
          rotulo="Data da coleta"
          type="date"
          value={coleta}
          onChange={(e) => setColeta(e.currentTarget.value)}
          erro={doCampo('dataColeta')}
        />

        <Campo
          rotulo="Onde a amostra fica"
          value={local}
          onChange={(e) => setLocal(e.currentTarget.value)}
          erro={doCampo('localizacaoFisica')}
          dica="Prateleira, sala, estante."
        />

        <Area
          rotulo="Por que esta amostra está entrando agora"
          value={motivo}
          onChange={(e) => setMotivo(e.currentTarget.value)}
          erro={doCampo('motivo')}
          rows={2}
          dica="Ex.: caixa ficou no veículo e foi entregue no dia seguinte."
          required
        />

        <Aviso tipo="atencao" titulo="O documento do recebimento não se atualiza sozinho">
          A amostra entra no lote e recebe código e etiqueta na hora. Se o PDF
          deste recebimento já foi emitido, ele continua como está: emitir a
          revisão nova é ato à parte, aqui na ficha.
        </Aviso>

        <div className="linha" style={{ marginTop: '1rem' }}>
          <button type="button" className="botao secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button
            type="submit" className="botao"
            disabled={enviando || motivo.trim().length < 5}
          >
            {enviando ? 'Gravando…' : 'Acrescentar'}
          </button>
        </div>
      </form>
    </Dialogo>
  );
}
